



















import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { IFootbowlAction } from './types';

const actionVideoContext = require.context(
  '@/assets/footbowl/actions',
  false,
  /\.(webm|mp4)$/,
);
const actionImageContext = require.context(
  '@/assets/footbowl/actions',
  false,
  /\.(jpg|png)$/,
);

const VIDEO_FALLBACK_TIMEOUT = 5000;

export const actionValidator = (obj: any) =>
  !obj ||
  (obj.name !== undefined &&
    obj.description !== undefined &&
    ['home', 'visitor', 'none'].includes(obj.team.toLowerCase()));

@Component({})
export default class FootbowlAction extends Vue {
  @Prop({ required: true, type: Boolean })
  public homeOnLeft!: boolean;

  @Prop({ required: false, type: Object, validator: actionValidator })
  public action?: IFootbowlAction;

  @Prop({ required: false, type: Number })
  public gain?: number;

  @Prop({ required: false, type: Number })
  public displayFor?: number;

  public actionVideoSources: string[] = [];
  public actionImage: any = null;

  @Watch('action', { immediate: true })
  public onActionChange(
    action?: IFootbowlAction,
    prevAction?: IFootbowlAction,
  ) {
    const intermediate = this.getIntermediateActionName(prevAction, action);
    if (intermediate && action) {
      const modified = { ...action, name: intermediate };
      this.displayAction(modified, () => {
        this.displayAction(action, () => {
          this.$emit('actionComplete');
        });
      });
    } else {
      this.displayAction(action, () => {
        this.$emit('actionComplete');
      });
    }
  }

  get displayGain() {
    return false;
  }

  private displayAction(action: IFootbowlAction | undefined, done: () => void) {
    const videoSources = this.getActionVideoSources(action);
    if (videoSources.length > 0) {
      this.actionVideoSources = videoSources;
      this.actionImage = null;
      this.$nextTick(() => {
        const video = this.$refs['action-video'] as HTMLVideoElement;
        const fallback = setTimeout(() => {
          done();
        }, VIDEO_FALLBACK_TIMEOUT);
        video.load();
        video.onplaying = () => {
          clearTimeout(fallback);
        };
        video.onended = () => {
          done();
        };
      });
    } else {
      const images = this.getRandomMatchingAsset(
        actionImageContext,
        this.action,
      );
      this.actionImage = images && images[0];
      this.actionVideoSources = [];
      setTimeout(() => {
        done();
      }, this.displayFor || 5000);
    }
  }

  private getIntermediateActionName(
    prev?: IFootbowlAction,
    current?: IFootbowlAction,
  ) {
    if (!prev || !current) {
      return null;
    }

    if (current.name === 'TOUCHDOWN') {
      switch (prev.name) {
        case 'PASS':
          return 'passing_touchdown';
        case 'RUN':
        case 'PITCH_PLAY':
        case 'REVERSE':
        case 'OFF_TACKLE':
          return 'running_touchdown';
      }
    }
    return null;
  }

  private tryLoadAction(name: string) {
    try {
      return require(`@/assets/footbowl/actions/${name}`) || null;
    } catch (e) {
      return null;
    }
  }

  private getRandomMatchingAsset(
    context: __WebpackModuleApi.RequireContext,
    action?: IFootbowlAction | null,
  ) {
    if (!action || !action.name) {
      return [];
    }

    const name = action.name.toLowerCase();
    const side =
      action.team.toLowerCase() === 'home'
        ? this.homeOnLeft
          ? 'left'
          : 'right'
        : this.homeOnLeft
          ? 'right'
          : 'left';

    // Look for assets in the format of `name.ext`, `name_[side].ext`, `name_[0-9].ext` or `name_[side]_[0-9].ext`
    const contextKeys = context.keys().map((key) => key.replace('./', ''));
    const sideSpecific = contextKeys.filter((key) =>
      key.startsWith(`${name}_${side}`),
    );
    const assets = sideSpecific.length
      ? sideSpecific
      : contextKeys.filter(
          (key) => key.startsWith(name + '_') || key.startsWith(name + '.'),
        );

    // Now group the assets (excluding the extension)
    const grouped = assets.reduce(
      (acc: Record<string, string[]>, filename: string) => {
        const key = filename.replace(/\.(.*)$/, '');
        return {
          ...acc,
          [key]: [...(acc[key] || []), filename],
        };
      },
      {} as Record<string, string[]>,
    );

    // Now pick a random key
    const keys = Object.keys(grouped);
    const selection = keys[Math.floor(Math.random() * keys.length)];

    // Now get the sources
    const sources = (grouped[selection] || [])
      .map((filename: string) => this.tryLoadAction(filename))
      .filter((src: string) => !!src);
    return sources.length ? sources : [];
  }

  private getActionVideoSources(action?: IFootbowlAction | null): string[] {
    const sources = this.getRandomMatchingAsset(actionVideoContext, action);
    return sources && sources.length ? sources : [];
  }
}
